import type { KanbanBoardTemplate } from '@/components/shared/kanban/types'
import type { Currency, Nullable } from '@/types'
import type DealStage from '@/data/models/domain/deals/DealStage'
import type { RegisteredServices } from '@/core/container/types'
import type { TmWrappers } from '@/wrappers'

export const DEAL_ATTACHMENTS_LIST_ID = 'dealAttachmentsListId' as RegisteredServices
export const DEAL_ATTACHMENTS_PER_PAGE = 10

export const DealsTable = 'DealsTable' as RegisteredServices

export const dealPipelineTemplates = [
  'blank',
  'sales',
  'recruitment',
  'procurement',
  'realEstate',
  'expenseTracking',
] as const

export type DealsPipelineTemplateId = (typeof dealPipelineTemplates)[number]

export type DealsPipelineTemplateStageId =
  | 'qualified'
  | 'contact-made'
  | 'proposal-made'
  | 'negotiation-started'
  | 'applied'
  | 'screening'
  | 'interview'
  | 'offer'
  | 'requests'
  | 'sourcing'
  | 'purchasing'
  | 'receiving'
  | 'new-leads'
  | 'lead-qualified'
  | 'schedule-showing'
  | 'negotiation'
  | 'signing-contract'
  | 'payment-requests'
  | 'approval'
  | 'payment'

export const dealsPipelineTemplateSystemStages = [
  'won',
  'lost',
  'hired',
  'rejected',
  'done',
  'paid',
  'canceled',
] as const
export type DealsPipelineTemplateSystemStageId = (typeof dealsPipelineTemplateSystemStages)[number]

export type DealPipelineTemplate = KanbanBoardTemplate<DealsPipelineTemplateId, DealsPipelineTemplateStageId> & {
  wonStageId: DealsPipelineTemplateSystemStageId
  lostStageId: DealsPipelineTemplateSystemStageId
}

export type CreateDealPipelinePayload = {
  name: string
  icon: string
  description: string
  currency: Currency
  stages: {
    customStages: Array<{ name: string; color: string; probability: number }>
    successStage: { name: string }
    failedStage: { name: string }
  }
}

export type CreateDealPayload = {
  title: string
  description?: string
  dealStageId: number
  amount: string | number
  assigneeId: Nullable<number>
  probability: number | null
  estimatedCloseDate?: string
  contactId?: number
}

export type UpdateDealPipelinePayload = Omit<CreateDealPipelinePayload, 'stages'>

export type CreateDealsStagePayload = {
  name: string
  color: string
  probability: number
  previousId: number | null
}

export type UpdateDealsStagePayload = {
  name: string
  color: string
  probability: number
}

export type ReorderPipelineStagesPayload = {
  pipelineId: string
  stage: DealStage
  prevStage: DealStage | null
}

export enum DealsPipelineView {
  LIST = 'list',
  KANBAN = 'kanban',
}

export type CreateTimelineItemPayload = {
  text: string
}

export type DealTimelineWrapperParams = {
  dealId: string
}

export const DEAL_TIMELINE_SECTION_WRAPPER: TmWrappers = 'dealTimelineSection'

export const DEAL_TIMELINE_ITEMS_PER_PAGE = 10
export const DEAL_TIMELINE_ITEM_LIST_ID = 'dealTimelineItemListId' as RegisteredServices

export const DEALS_LIST_ID = 'dealsListId' as RegisteredServices
export const DEALS_PER_PAGE: number = 999

export type DealsFilters = 'stage' | 'assignee' | 'contact' | 'estimatedCloseDate' | 'amount' | 'probability'
